// File: src/components/JobRequirements/MappedStudents.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../styles/MappedStudents.css';
import JobDetailsCard from './MatchingStudents/JobDetailsCard';
import MappedStudentsTable from './MappedStudents/MappedStudentsTable';
import MappedFiltersSection from './MappedStudents/MappedFiltersSection';

const MappedStudents = ({ jobReqId, jobDetails }) => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState([]);
    const [filters, setFilters] = useState({
        status: 'all',
        studentName: '',
        branch: [],
        acknowledgementStatus: [],
        placementInterest: [],
        fromDate: '',
        toDate: ''
    });
    const [statusCounts, setStatusCounts] = useState({
        all: 0,
        Assigned: 0,
        Shortlisted: 0,
        Selected: 0,
        Rejected: 0,
        Interviewed: 0,
        'On Hold': 0
    });

    const [selectedIds, setSelectedIds] = useState(new Set());

    // ── NEW: keep the full unfiltered dataset so we can always find selected rows ──
    const allStudentsRef = useRef([]);

    const hasFetchedRef = useRef(false);
    const componentMountedRef = useRef(true);
    const abortControllerRef = useRef(null);

    // ── NEW: build the merged display list ──
    // Selected students always appear first (even if they don't match the current filter),
    // followed by the filtered results that are NOT already selected.
    const displayStudents = React.useMemo(() => {
        if (selectedIds.size === 0) return students;

        const selectedRows = allStudentsRef.current.filter(s =>
            selectedIds.has(s.mappingId || s.id)
        );

        const filteredNotSelected = students.filter(s =>
            !selectedIds.has(s.mappingId || s.id)
        );

        return [...selectedRows, ...filteredNotSelected];
    }, [students, selectedIds]);

    const fetchMappedStudents = useCallback(async () => {
        if (!jobReqId || !componentMountedRef.current || hasFetchedRef.current) return;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/mapped-students/company/${jobReqId}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    signal: abortControllerRef.current.signal
                }
            );

            if (response.ok) {
                const result = await response.json();

                if (result.success && componentMountedRef.current) {
                    const allData = result.data || [];

                    // ── NEW: always keep a fresh copy of the full dataset ──
                    allStudentsRef.current = allData;

                    const counts = {
                        all: allData.length,
                        Assigned: 0,
                        Acknowledged: 0,
                        Shortlisted: 0,
                        'Interview Scheduled': 0,
                        'Resume Not Shortlisted': 0,
                        'Not Attended': 0,
                        'Not Selected': 0,
                        Selected: 0,
                        'Rejected By Student': 0,
                        Cancelled: 0,
                    };
                    allData.forEach(student => {
                        if (counts.hasOwnProperty(student.currentStatus)) {
                            counts[student.currentStatus]++;
                        }
                    });
                    setStatusCounts(counts);

                    let filteredList = allData;
                    if (filters.status !== 'all') {
                        filteredList = filteredList.filter(s => s.currentStatus === filters.status);
                    }
                    if (filters.acknowledgementStatus.length > 0) {
                        filteredList = filteredList.filter(s =>
                            filters.acknowledgementStatus.includes(s.acknowledgementStatus)
                        );
                    }
                    if (filters.branch.length > 0) {
                        filteredList = filteredList.filter(s =>
                            filters.branch.includes(s.branch)
                        );
                    }
                    if (filters.placementInterest.length > 0) {
                        filteredList = filteredList.filter(s =>
                            filters.placementInterest.includes(s.placementInterest)
                        );
                    }
                    if (filters.fromDate) {
                        filteredList = filteredList.filter(s =>
                            new Date(s.createdAt) >= new Date(filters.fromDate)
                        );
                    }

                    if (filters.toDate) {
                        filteredList = filteredList.filter(s =>
                            new Date(s.createdAt) <= new Date(filters.toDate)
                        );
                    }
                    if (filters.studentName) {
                        filteredList = filteredList.filter(s =>
                            s.studentName?.toLowerCase().includes(filters.studentName.toLowerCase())
                        );
                    }

                    setStudents(filteredList);

                    // Keep selected IDs that still exist in the full dataset
                    const freshIdSet = new Set(allData.map(s => s.mappingId || s.id));
                    setSelectedIds(prev => {
                        if (prev.size === 0) return prev;
                        const kept = new Set([...prev].filter(id => freshIdSet.has(id)));
                        return kept.size === prev.size ? prev : kept;
                    });

                    hasFetchedRef.current = true;
                }
            } else {
                console.error('Failed to fetch mapped students:', response.status);
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Fetch aborted');
            } else {
                console.error('Error fetching mapped students:', error);
            }
        } finally {
            if (componentMountedRef.current) {
                setLoading(false);
            }
        }
    }, [jobReqId, filters.status, filters.acknowledgementStatus, filters.studentName, filters.branch, filters.placementInterest,
        filters.fromDate, filters.toDate]);

    useEffect(() => {
        componentMountedRef.current = true;
        if (jobReqId && !hasFetchedRef.current) {
            fetchMappedStudents();
        }
        return () => {
            componentMountedRef.current = false;
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [jobReqId]);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/location`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                if (Array.isArray(data)) setBranches(data);
            } catch (err) {
                console.error("Error fetching branches:", err);
            }
        };
        fetchBranches();
    }, []);

    const handleFilterChange = useCallback((name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleApplyFilters = useCallback(() => {
        hasFetchedRef.current = false;
        fetchMappedStudents();
    }, [fetchMappedStudents]);

    const handleClearFilters = useCallback(() => {
        setFilters({
            status: 'all',
            studentName: '',
            branch: [],
            acknowledgementStatus: [],
            placementInterest: [],
            fromDate: '',
            toDate: ''
        });
        hasFetchedRef.current = false;
        // selectedIds intentionally NOT cleared
    }, []);

    useEffect(() => {
        if (
            !hasFetchedRef.current &&
            filters.status === 'all' &&
            filters.studentName === '' &&
            filters.branch.length === 0 &&
            filters.acknowledgementStatus.length === 0 &&
            filters.placementInterest.length === 0 &&
            filters.fromDate === '' &&
            filters.toDate === ''
        ) {
            fetchMappedStudents();
        }
    }, [filters, fetchMappedStudents]);

    const handleStatusUpdate = useCallback(() => {
        hasFetchedRef.current = false;
        fetchMappedStudents();
    }, [fetchMappedStudents]);

    const isAnyFilterApplied =
        filters.status !== 'all' ||
        filters.studentName !== '' ||
        filters.branch.length > 0 ||
        filters.placementInterest.length > 0 ||
        filters.fromDate !== '' ||
        filters.toDate !== '' ||
        filters.acknowledgementStatus.length > 0;

    const LoadingSkeleton = () => (
        <div className="loading-skeleton">
            <div className="skeleton-job-details">
                <div className="skeleton-title"></div>
                <div className="skeleton-grid">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="skeleton-detail"></div>
                    ))}
                </div>
            </div>
            <div className="skeleton-filters">
                <div className="skeleton-title"></div>
                <div className="skeleton-filter-grid">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="skeleton-filter"></div>
                    ))}
                </div>
            </div>
            <div className="skeleton-table">
                <div className="skeleton-table-header"></div>
                <div className="skeleton-table-rows">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="skeleton-table-row"></div>
                    ))}
                </div>
            </div>
        </div>
    );

    if (loading && !hasFetchedRef.current) {
        return <LoadingSkeleton />;
    }

    return (
        <div className="mapped-students-container">
            <JobDetailsCard jobDetails={jobDetails} />

            <MappedFiltersSection
                filters={filters}
                statusCounts={statusCounts}
                branches={branches}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
            />

            <MappedStudentsTable
                // ── CHANGED: pass displayStudents (merged) instead of students ──
                students={displayStudents}
                loading={loading}
                hasFetchedRef={hasFetchedRef}
                isAnyFilterApplied={isAnyFilterApplied}
                onClearFilters={handleClearFilters}
                onStatusUpdate={handleStatusUpdate}
                jobReqId={jobReqId}
                currentStatus={filters.status}
                acknowledgementStatus={filters.acknowledgementStatus}
                branch={filters.branch}
                studentName={filters.studentName}
                placementInterest={filters.placementInterest}
                fromDate={filters.fromDate}
                toDate={filters.toDate}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
            />
        </div>
    );
};

export default MappedStudents;